/**
 * Application entry.
 *
 * The one import that matters here is the first: `design/project/styles/tokens.css` is the
 * canonical token file and is consumed verbatim, from the design folder, imported exactly
 * once for the whole app (MCK-20260521-003 AC 1). It is not copied into this package and
 * its values are never re-keyed — `scripts/guard.mjs` fails the build on any hex literal
 * in `src/`, which is what makes "verbatim" a fact rather than an intention.
 */

// Before the tokens, so the `@font-face` rules exist by the time anything asks for
// `--font-sans`. See `styles/fonts.ts` for why the CDN `@import` inside `tokens.css` is
// stripped at build time rather than relied on.
import './styles/fonts';
import '@design/project/styles/tokens.css';
import './styles/base.css';

import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { markBoot } from '@/lib/boot';
import { loadPack, readStoredLocale } from '@/lib/i18n';

import { AppProviders } from './providers';
import { router } from './router';

const container = document.getElementById('root');
if (container === null) throw new Error('Missing #root element in index.html.');

// Before `createRoot`, and this is the only moment it can be asked: the first commit replaces
// `#root`'s static children, so the boot loader is gone before any component could look for it.
// `lib/boot.ts` says what the answer is then used for.
markBoot(container.querySelector('.kalqa-boot') !== null);

/**
 * The first render waits for the reader's language.
 *
 * The packs are `import()`s now (`lib/i18n/catalog.ts`), so the language the store will start
 * in — the same `readStoredLocale()` it reads — may not be in memory when this module runs.
 * Rendering before it lands would draw every screen in English and re-draw it a round trip
 * later, which is the one thing CLAUDE.md's loading rule forbids a partial load to do. Waiting
 * costs nothing visible: `index.html`'s boot loader is on screen and stays there until the
 * first commit (`lib/boot.ts`), which is exactly the "nothing known yet" moment the page loader
 * is reserved for. A pack that fails to fetch is not a blank page — the catch renders English,
 * per key, as the fallback has always done, and the store retries on the next switch.
 */
const root = createRoot(container);
void loadPack(readStoredLocale())
  .catch(() => undefined)
  .then(() => {
    root.render(
      <StrictMode>
        <AppProviders>
          <RouterProvider router={router} />
        </AppProviders>
      </StrictMode>,
    );
  });
